/**
 * All Words View
 *
 * Admin view for browsing and managing all words in the global word bank.
 * Provides search, filtering by language, and coverage status indicators.
 */

import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useInitialData } from '../../../contexts/InitialDataContext';
import { useAllWords } from '../services/wordsApi';
import { WordsNavigation } from '../components/WordsNavigation';
import type { WordWithCoverage } from '../types/word.types';

export function AllWordsView() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const pageSize = 20;

  // Get language filter from URL params (set by WordsNavigation)
  const languageFilter = searchParams.get('lang') || '';

  const { state: initialDataState } = useInitialData();
  const languages = initialDataState.data?.languages ?? [];

  const { data, isLoading, error } = useAllWords(
    page,
    pageSize,
    debouncedSearch || undefined,
    languageFilter || undefined
  );

  // Debounce search input
  const handleSearchChange = (value: string) => {
    setSearch(value);
    // Simple debounce using setTimeout
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
    return () => clearTimeout(timeoutId);
  };

  const getLanguageName = (code: string) => {
    const lang = languages.find((l) => l.code === code);
    return lang?.name ?? code;
  };

  const getCoverageStatus = (word: WordWithCoverage) => {
    const statuses = [];
    if (!word.hasImage) statuses.push('image');
    if (!word.hasTranslations) statuses.push('translations');
    if (!word.hasPronunciations) statuses.push('pronunciations');
    if (!word.hasExample) statuses.push('example');
    return statuses;
  };

  const handleWordClick = (word: WordWithCoverage) => {
    navigate(
      `/words/${encodeURIComponent(word.word)}/${word.languageCode}/edit?cardId=${word.cardId}`
    );
  };

  const handleAddWord = () => {
    const langParam = languageFilter ? `?lang=${languageFilter}` : '';
    navigate(`/words/new${langParam}`);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <WordsNavigation />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <WordsNavigation />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <p className="text-red-600 text-lg mb-4">Error loading words</p>
            <p className="text-gray-600">{(error as Error).message}</p>
          </div>
        </div>
      </div>
    );
  }

  const totalPages = data ? Math.ceil(data.total / pageSize) : 0;

  return (
    <div className="container mx-auto px-4 py-8">
      <WordsNavigation />

      {/* Header */}
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold">All Words</h1>
          <p className="text-gray-600 mt-1">
            Browse and manage all words in the global word bank across all languages.
          </p>
        </div>
        <button
          onClick={handleAddWord}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Word
        </button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="max-w-md">
          <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
          <input
            type="text"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search words..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="mb-4 text-sm text-gray-600">{data?.total ?? 0} words total</div>

      {/* Words Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Word
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Language
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Coverage
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data?.items.map((word) => {
              const missingCoverage = getCoverageStatus(word);
              const isComplete = missingCoverage.length === 0;

              return (
                <tr
                  key={word.cardId}
                  data-testid={`word-row-${word.word}`}
                  onClick={() => handleWordClick(word)}
                  className="cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {word.imageUrl && (
                        <img
                          src={word.imageUrl}
                          alt={word.word}
                          className="w-10 h-10 rounded object-cover"
                        />
                      )}
                      <div>
                        <div className="text-sm font-medium text-gray-900">{word.word}</div>
                        {word.exampleSentence && (
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {word.exampleSentence}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {getLanguageName(word.languageCode)}
                  </td>
                  <td className="px-6 py-4">
                    {isComplete ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Complete
                      </span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {missingCoverage.map((missing) => (
                          <span
                            key={missing}
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"
                          >
                            needs {missing}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {new Date(word.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Empty State */}
      {data?.items.length === 0 && (
        <div className="bg-white rounded-lg shadow-md p-12 text-center mt-4">
          <p className="text-gray-600 text-lg">
            {search || languageFilter
              ? 'No words found matching your filters.'
              : 'No words in the database yet.'}
          </p>
          <p className="text-gray-500 mt-2">
            Words are added when you create word lists or import vocabulary.
          </p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, data?.total ?? 0)} of{' '}
            {data?.total ?? 0} results
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <span className="px-3 py-1">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
